import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import TrashBin from '@components/icons/TrashBin.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';

import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SmsCountriesService } from 'app/API';
import { smsKeys } from 'app/queries/sms/smsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { Helmet } from 'react-helmet-async';

import { createFetchSmsCountriesQueryFn } from '../../api/fetchSmsCountries';

import { Tabs } from './components/Tabs';
import { CellContent } from './styles/CellContent';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { Wrapper } from './styles/Wrapper';

export const Content = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();

  const countriesQuery = useQuery({
    queryKey: smsKeys.countries(),
    queryFn: createFetchSmsCountriesQueryFn(call),
  });

  const removeMutation = useMutation({
    mutationFn: (code: string) => call(() => SmsCountriesService.remove({ code })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: smsKeys.countries() }),
  });

  const createCountry = () => {
    openModal(ModalNamesEnum.CreateNewSMSCountry, {
      onSuccess: () => {
        closeModal();
        void queryClient.invalidateQueries({ queryKey: smsKeys.countries() });
      },
    });
  };

  const removeCountry = (code: string) => {
    const confirmation = window.confirm('Are you sure you want to remove this country?');

    if (!confirmation) {
      return;
    }
    removeMutation.mutate(code);
  };

  const smsCountries = countriesQuery.data ?? [];
  const isLoading = countriesQuery.isFetching || removeMutation.isPending;

  return (
    <>
      <Helmet>
        <title>SMS Countries list - AutoScript Admin</title>
      </Helmet>
      <Header
        title={'SMS OTP Country Rules'}
        buttonLabel={'Create new rule'}
        buttonOnClick={() => createCountry()}
        leftSideContent={[<Tabs key="SMSTabs" currentTab="SMS Countries" />]}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Country</Th>
                <Th>Prefix</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {smsCountries.map((data, index) => (
                <Tr key={data.code}>
                  <Td>
                    <CellContent>{index + 1}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      {' '}
                      {data.code ? getUnicodeFlagIcon(data.code) : null} {data.code}
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.prefix}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.status}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      <IconButtonDelete title="Remove" onClick={() => removeCountry(data.code)}>
                        <TrashBin />
                      </IconButtonDelete>
                    </CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    </>
  );
};
